<template>
  <div style="display: inline">
    <el-button :disabled="directDoneInvoiceLoading" @click="markInvoiced" class="d-flex-row funcbars-button"
               v-permission="'can_direct_invoice'" size="mini" style="margin-right: 10px;">
      <i class="el-icon-finished"></i>标记开票
    </el-button>
    <el-dialog :append-to-body="true" title="标记开票" :visible.sync="dialog.show" width="1100px" center
               custom-class="mark-invoice-dialog">
      <div class="import-bar">
        <el-button v-if="!preselected" size="mini" type="primary" plain icon="el-icon-upload2" :loading="importing"
                   @click="triggerImport">导入 Excel</el-button>
        <el-button v-if="!preselected" size="mini" type="text" @click="downloadTemplate">下载模板</el-button>
        <span class="import-tip">
          <template v-if="rows.length">
            已导入 {{ rows.length }} 条（就绪 {{ readyRows.length }} / 异常 {{ errorRows.length }}）
          </template>
          <template v-else>
            表格两列：订单编号（必填）、平台订单编号（选填）；单次上限 {{ MAX_ROWS }} 条
          </template>
        </span>
        <input ref="fileInput" type="file" accept=".xlsx,.xls" style="display: none" @change="handleFile"/>
      </div>
      <el-table :data="rows" height="360" size="mini" class="mark-invoice-table"
                empty-text="暂无数据，请点击「导入 Excel」导入待开票订单">
        <el-table-column type="index" label="序号" width="56" align="center"/>
        <el-table-column prop="order_no" label="订单编号" min-width="210" show-overflow-tooltip/>
        <el-table-column label="品牌" width="120" show-overflow-tooltip>
          <template v-slot="{$index}">{{ rows[$index].ok ? (rows[$index].brand || '—') : '—' }}</template>
        </el-table-column>
        <el-table-column label="红人名称" width="120" show-overflow-tooltip>
          <template v-slot="{$index}">{{ rows[$index].ok ? (rows[$index].artist_nickname || '—') : '—' }}</template>
        </el-table-column>
        <el-table-column label="含税收入" align="right" width="130">
          <template v-slot="{$index}">
            <span v-if="rows[$index].ok">{{ rows[$index].payment_account | numberFormat(2) }}</span>
            <span v-else>—</span>
          </template>
        </el-table-column>
        <el-table-column label="平台订单编号" width="210">
          <template v-slot="{$index}">
            <el-input v-if="rows[$index].ok" v-model="rows[$index].oth_order_no" size="mini"
                      placeholder="选填" clearable maxlength="100"/>
            <span v-else class="text-muted">{{ rows[$index].oth_order_no || '—' }}</span>
          </template>
        </el-table-column>
        <el-table-column label="状态" width="150">
          <template v-slot="{$index}">
            <span v-if="rows[$index].ok" class="text-success">就绪</span>
            <span v-else class="text-danger">{{ rows[$index].error }}</span>
          </template>
        </el-table-column>
      </el-table>
      <div class="tip">
        即将标记开票 {{ readyRows.length }} 条订单，合计含税收入 ￥{{ moneySum | numberFormat(2) }}
        <span v-if="errorRows.length" class="text-danger">，异常 {{ errorRows.length }} 条不提交</span>
      </div>
      <div slot="footer" class="dialog-footer">
        <el-button v-if="errorRows.length" size="mini" @click="clearErrorRows">清除异常行</el-button>
        <el-button @click="dialog.show = false">取 消</el-button>
        <button-submit button-txt="确 定" type="primary" :disabled="!readyRows.length"
                       @click.native.prevent="handelSure"/>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import {directInvoice} from "@/api/bill";
import {queryOrdersList} from "@/api/adOrder";
import XLSX from "xlsx";

// 单次导入上限
const MAX_ROWS = 100;
// 可开票的订单类型
const INVOICE_ABLE_TYPES = ['regular', '普通类型'];

export default {
  name: "MarkOrderInvoiceOpt",
  props: {
    dataList: {
      type: Array,
      default() {
        return []
      }
    }
  },
  data() {
    return {
      MAX_ROWS,
      dialog: {
        show: false
      },
      // 弹窗内维护的行数据（不直接改动 props）
      rows: [],
      directDoneInvoiceLoading: false,
      importing: false,
      // 打开弹窗时是否已带入勾选的订单（true 时隐藏 导入/下载模板 按钮）
      preselected: false
    }
  },
  computed: {
    readyRows() {
      return this.rows.filter(row => row.ok)
    },
    errorRows() {
      return this.rows.filter(row => !row.ok)
    },
    moneySum() {
      return this.readyRows.reduce((sum, row) => sum + (parseFloat(row.payment_account) || 0), 0)
    }
  },
  watch: {
    "dialog.show"(_) {
      if (!_) {
        this.directDoneInvoiceLoading = false;
      }
    }
  },
  methods: {
    // 打开弹窗：有勾选订单时带出来，没有则打开空表等待导入
    markInvoiced() {
      this.preselected = (this.dataList || []).length > 0;
      this.rows = (this.dataList || []).map(item => this.buildRowFromOrder(item, item.oth_order_no, true));
      this.dialog.show = true;
    },
    // 用系统订单构造一行
    buildRowFromOrder(order, othOrderNo, keepExistValue) {
      const error = this.validateOrder(order);
      let oth = othOrderNo;
      if ((oth === undefined || oth === null) && keepExistValue) {
        oth = order.oth_order_no;
      }
      return {
        order_no: order.order_no,
        brand: order.brand,
        artist_nickname: order.artist_nickname,
        payment_account: order.payment_account,
        oth_order_no: oth === undefined || oth === null ? '' : String(oth),
        ok: !error,
        error: error
      }
    },
    // 订单是否允许标记开票
    validateOrder(order) {
      if (!order) {
        return '未找到该订单';
      }
      if (INVOICE_ABLE_TYPES.indexOf(order.order_type) === -1) {
        return '非普通订单，不能开票';
      }
      if (String(order.is_mark_invoiced) === 'Y') {
        return '该订单已标记开票';
      }
      if (order.bool_undo) {
        return '该订单已撤单';
      }
      return '';
    },
    triggerImport() {
      this.$refs.fileInput.click();
    },
    // 选择文件后解析
    handleFile(e) {
      const file = e.target.files && e.target.files[0];
      // 允许重复选择同一个文件
      e.target.value = '';
      if (!file) {
        return;
      }
      if (!/\.(xlsx|xls)$/i.test(file.name)) {
        this.msg('只支持 .xlsx / .xls 文件');
        return;
      }
      this.importing = true;
      const reader = new FileReader();
      reader.onload = async (ev) => {
        try {
          const workbook = XLSX.read(ev.target.result, {type: 'binary', codepage: 936});
          const sheet = workbook.Sheets[workbook.SheetNames[0]];
          const matrix = XLSX.utils.sheet_to_json(sheet, {header: 1, raw: false, defval: ''});
          await this.applyMatrix(matrix);
        } catch (err) {
          this.msg('文件解析失败：' + ((err && err.message) || err));
        } finally {
          this.importing = false;
        }
      };
      reader.onerror = () => {
        this.importing = false;
        this.msg('文件读取失败');
      };
      reader.readAsBinaryString(file);
    },
    // 处理表格矩阵
    async applyMatrix(matrix) {
      if (!matrix || !matrix.length) {
        this.msg('文件内容为空');
        return;
      }
      // 定位列：优先按表头名，找不到则按第 1、2 列
      const header = (matrix[0] || []).map(cell => String(cell === null || cell === undefined ? '' : cell).trim());
      let colOrderNo = header.findIndex(text => text.indexOf('平台') === -1 && text.indexOf('订单编号') > -1);
      let colOthNo = header.findIndex(text => text.indexOf('平台订单编号') > -1);
      let startRow = 1;
      if (colOrderNo === -1 || colOthNo === -1) {
        colOrderNo = 0;
        colOthNo = 1;
        startRow = 0;
      }

      // 逐行解析：同一订单编号以最后一行为准
      const pickedMap = new Map();
      const emptyRows = [];
      for (let i = startRow; i < matrix.length; i++) {
        const row = matrix[i] || [];
        const orderNo = this.clean(row[colOrderNo]);
        const othNo = this.clean(row[colOthNo]);
        if (!orderNo && !othNo) {
          // 整行为空，忽略
          continue;
        }
        if (!orderNo) {
          // 平台订单编号选填；没有订单编号则无法匹配
          emptyRows.push({order_no: '', oth_order_no: othNo, error: '订单编号为空'});
          continue;
        }
        pickedMap.set(orderNo, othNo);
      }

      // 已存在的订单：直接更新平台订单编号，不重复加行
      const existMap = new Map();
      this.rows.forEach(row => existMap.set(row.order_no, row));
      const toQuery = [];
      pickedMap.forEach((othNo, orderNo) => {
        const exist = existMap.get(orderNo);
        if (exist) {
          exist.oth_order_no = othNo;
        } else {
          toQuery.push([orderNo, othNo]);
        }
      });

      if (this.rows.length + toQuery.length > MAX_ROWS) {
        this.msg(`单次上限 ${MAX_ROWS} 条，当前会达到 ${this.rows.length + toQuery.length} 条，请分批导入`);
        return;
      }

      // 一次性查询订单（上限 100 条，正好覆盖单次导入上限）
      let orders = [];
      if (toQuery.length) {
        const res = await queryOrdersList({
          order_nos: toQuery.map(item => item[0]).join(','),
          page: 1,
          per_page: MAX_ROWS
        });
        if (!res || !res.success) {
          this.msg((res && res.message) || '查询订单失败');
          return;
        }
        orders = (res.model && res.model.data) || [];
      }

      const orderMap = new Map();
      orders.forEach(order => orderMap.set(this.clean(order.order_no), order));

      const addedRows = toQuery.map(item => {
        const orderNo = item[0];
        const othNo = item[1];
        const order = orderMap.get(orderNo);
        if (!order) {
          return {
            order_no: orderNo,
            brand: '',
            artist_nickname: '',
            payment_account: '',
            oth_order_no: othNo,
            ok: false,
            error: '未找到该订单'
          };
        }
        return this.buildRowFromOrder(order, othNo, false);
      });

      const invalidRows = emptyRows.map(item => ({
        order_no: item.order_no,
        brand: '',
        artist_nickname: '',
        payment_account: '',
        oth_order_no: item.oth_order_no,
        ok: false,
        error: item.error
      }));

      this.rows = this.rows.concat(addedRows, invalidRows);

      const okCount = addedRows.filter(row => row.ok).length;
      const errCount = addedRows.filter(row => !row.ok).length + invalidRows.length;
      this.msg(`导入完成：就绪 ${okCount} 条，异常 ${errCount} 条`, errCount ? 'warning' : 'success');
    },
    // 单元格转字符串并去空白
    clean(value) {
      if (value === null || value === undefined) {
        return '';
      }
      return String(value).replace(/^\s+|\s+$/g, '').replace(/^\u3000+|\u3000+$/g, '');
    },
    // 下载空模板
    downloadTemplate() {
      const sheet = XLSX.utils.aoa_to_sheet([['订单编号', '平台订单编号'], ['A1879809696529909', '']]);
      sheet['!cols'] = [{wch: 24}, {wch: 24}];
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, sheet, '平台订单编号');
      XLSX.writeFile(workbook, '平台订单编号导入模板.xlsx');
    },
    // 删除异常行
    clearErrorRows() {
      this.rows = this.readyRows;
    },
    // 提交
    async handelSure() {
      const ready = this.readyRows;
      if (!ready.length) {
        this.msg('请先导入待开票订单');
        return;
      }
      if (this.errorRows.length) {
        const tip = `将标记开票 ${ready.length} 条订单，${this.errorRows.length} 条异常不提交，是否继续？`;
        const goOn = await this.$confirm(tip, '提示', {
          confirmButtonText: '继续',
          cancelButtonText: '取消',
          type: 'warning'
        }).then(() => true).catch(() => false);
        if (!goOn) {
          return;
        }
      }
      const data = ready.map(row => {
        return {
          order_no: row.order_no,
          // 平台订单编号选填：留空时传 null，保持与原有数据一致
          oth_order_no: this.clean(row.oth_order_no) || null
        }
      });
      await this.commitReq(data);
    },
    // 提交请求
    async commitReq(data) {
      const that = this;
      const response = await directInvoice({data});
      if (!response.success) {
        this.msg(response.message || '操作失败', 'error', 3000);
        return;
      }
      that.dialog.show = false;
      that.rows = [];
      this.showToast({h: that, icon: 'el-icon-success', text: '操作成功', duration: 2000});
      // 标记完成
      that.$emit('marked');
    },
    msg(text, type, duration) {
      let icon = 'el-icon-error';
      if (type === 'success') {
        icon = 'el-icon-success';
      } else if (type === 'warning') {
        icon = 'el-icon-warning';
      }
      this.showToast({h: this, icon: icon, text: text, duration: duration || 2500});
    }
  }
}
</script>

<style lang="scss" scoped>
.import-bar {
  display: flex;
  align-items: center;
  padding: 0 0 10px 0;

  .import-tip {
    margin-left: 10px;
    color: #909399;
    font-size: 12px;
  }
}

.text-success {
  color: #67c23a;
}

.text-danger {
  color: #f56c6c;
}

.text-muted {
  color: #c0c4cc;
}

.tip {
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 8px 18px;
  //height: 32px;
  border-radius: 0 0 9px 9px;
  background-image: linear-gradient(270deg, rgba(255, 0, 0, 0.06) 0%, rgba(61, 127, 255, 0.09) 100%);
}

</style>
<style lang="scss">
.mark-invoice-table thead tr, .mark-invoice-table thead th {
  background: #ffffff !important;
  color: #020202 !important;
}

/* 数据行稍微调高 */
.mark-invoice-table .el-table__body tr.el-table__row td {
  padding-top: 13px;
  padding-bottom: 13px;
}

.mark-invoice-table {
  th {
    .cell {
      padding-bottom: 8px;
      font-weight: 600;
      color: #606266;
    }
  }
}

.mark-invoice-dialog {
  max-width: 96vw;

  .el-dialog__body {
    padding: 20px 30px 0 30px;
  }
}
</style>
